import Vue from 'vue';
import api from '@/api'
import _ from 'lodash';
import LayoutConverter from '@/libs/layout-converter'

export default {

  state: {
    departments: {
      levels: []
    },
    accreditations: {
      documents: []
    },
    tenders: {
      documents: []
    },
    lotNumbers: {
      total: undefined,
      items: []
    },
    expertCriteria: {
      items: []
    },
    priceCriteria: {
      items: []
    },
    weightedCriteria: {
      items: []
    },
    fileExtensions: {
      items: []
    },
    additionalExpenses: {
      items: []
    },
    contractors: {
      total: undefined,
      items: []
    },
    templates: {
      items: []
    },
    nomenclature: {
      items: []
    },
    bidRules: {
      items: []
    },
    additionalBidRules: {
      items: []
    },
    regulationsParagraphs: {
      items: []
    },
    blackBox: {
      total: undefined,
      items: []
    },

  },

  mutations: {
    setDepartments(s, data) {
      let level = 1;
      let items = [];

      do {
        items = _.filter(data, { level });

        if (items) {
          s.departments.levels[level] = items;
        }

        level++;
      } while (items.length > 0);
    },

    setAccreditationDocuments(s, data) {
      s.accreditations.documents = data;
    },

    setTenderDocuments(s, data) {
      s.tenders.documents = data;
    },

    setLotNumbers(s, data) {
      s.lotNumbers.total = data.total;
      s.lotNumbers.items = data.data;
    },

    setExpertCriteria(s, data) {
      s.expertCriteria.items = data.map(e => ({
        id: e.id,
        name: e.name,
        isText: e.valueType === 'Text' || e.valueType === 'All',
        isFile: e.valueType === 'File' || e.valueType === 'All'
      }));
    },

    setPriceCriteria(s, data) {
      s.priceCriteria.items = data.map(e => ({
        id: e.id,
        name: e.name,
        type: e.type,
        isText: e.valueType === 'Text' || e.valueType === 'All',
        isFile: e.valueType === 'File' || e.valueType === 'All'
      }));
    },

    setWeightedCriteria(s, data) {
      s.weightedCriteria.items = data.map(e => ({
        id: e.id,
        name: e.name
      }));
    },

    setFileExtensions(s, data) {
      s.fileExtensions.items = data;
    },

    setAdditionalExpenses(s, data) {
      s.additionalExpenses.items = data.map(e => ({
        id: e.id,
        name: e.name,
        code: e.code,
        formula: new api.BidRuleFormula(!!e.percent),
      }));
    },

    setContractors(s, data) {
      s.contractors.total = data.total;
      s.contractors.items = data.data;
    },

    setTemplates(s, data) {
      s.templates.items = data;
    },

    clearNomenclatureCatalog(s) {
      s.nomenclature.items = [];
    },

    setNomenclature(s, data) {
      const idx = _.findIndex(s.nomenclature.items, { id: data.parent });
      const level = _.get(s.nomenclature.items[idx], 'level', -1);

      s.nomenclature.items.splice(idx + 1, 0, ...data.items.map(e => ({
        ...e,
        parent: data.parent,
        level: level + 1,
        collapsed: true,
        fetchedChildren: false
      })));

      if (level !== -1) {
        Vue.set(s.nomenclature.items, idx, {
          ...s.nomenclature.items[idx],
          fetchedChildren: true
        });
      }
    },

    toggleNomenclature(s, data) {
      const idx = _.findIndex(s.nomenclature.items, { id: data.id });

      Vue.set(s.nomenclature.items, idx, {
        ...s.nomenclature.items[idx],
        collapsed: !s.nomenclature.items[idx].collapsed
      });
    },

    setBidRules(s, data) {
      s.bidRules.items = data.map(e => ({
        id: e.id,
        allowAdvance: !!e.allowAdvance,
        bidCurrency: new api.BynOrAnotherCurrency(!!e.bidByn),
        bidRuleComment: e.bidRuleComment,
        bidRuleUserComment: e.bidRuleUserComment,
        currencyClause: new api.CurrencyClause(!!e.currencyClause),
        fixBynComment: e.fixBynComment,
        fixDate: new api.FixDateChoice(e.fixDate),
        contractCurrency: new api.BynOrAnotherCurrency(!!e.payByn),
        paymentCurrency: e.payBynComment,
        paymentDate: new api.PaymentDateChoice(e.payDate),
        status: new api.CatalogPersonStatus(e.resident),
        type: new api.BidRuleType(e.type)
      }));
    },

    setAdditionalBidRules(s, data) {
      s.additionalBidRules.items = data.map(item => ({
        nomen: item.nomen,
        rules: item.rules.map(e => ({
          id: e.id,
          allowAdvance: !!e.allowAdvance,
          bidCurrency: new api.BynOrAnotherCurrency(!!e.bidByn),
          bidRuleComment: e.bidRuleComment,
          bidRuleUserComment: e.bidRuleUserComment,
          currencyClause: new api.CurrencyClause(!!e.currencyClause),
          fixBynComment: e.fixBynComment,
          fixDate: new api.FixDateChoice(e.fixDate),
          contractCurrency: new api.BynOrAnotherCurrency(!!e.payByn),
          paymentCurrency: e.payBynComment,
          paymentDate: new api.PaymentDateChoice(e.payDate),
          status: new api.CatalogPersonStatus(e.resident),
          type: new api.BidRuleType(e.type),
          rule: e.rule
        }))
      }));
    },

    setRegulationsParagraphs(s, data) {
      s.regulationsParagraphs.items = data;
    },
    setBlackBox(s, data) {
      s.blackBox.total = data.total;
      s.blackBox.items = data.data;
    },
  },

  getters: {
    departments: s => s.departments,
    accreditations: s => s.accreditations,
    tenders: s => s.tenders,
    lotNumbers: s => s.lotNumbers,
    expertCriteria: s => s.expertCriteria,
    priceCriteria: s => s.priceCriteria,
    weightedCriteria: s => s.weightedCriteria,
    fileExtensions: s => s.fileExtensions,
    additionalExpenses: s => s.additionalExpenses,
    contractors: s => s.contractors,
    templatesCatalog: s => s.templates,
    nomenclature: s => s.nomenclature,
    bidRules: s => s.bidRules,
    additionalBidRules: s => s.additionalBidRules,
    regulationsParagraphs: s => s.regulationsParagraphs,
    blackBox: s => s.blackBox
  },

  actions: {
    async fetchDepartmentsFromCatalog({commit}) {
      try {
        const response = await api.get('/system/departments');
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setDepartments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async deleteDepartmentFromCatalog({dispatch}, { id }) {
      try {
        const response = await api.delete(`/system/department/${id}`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          await dispatch('fetchDepartmentsFromCatalog');
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addDepartmentToCatalog({dispatch}, { name, parentId, level }) {
      try {
        const response = await api.put(`/system/department/0`, {
          name,
          parentId,
          level
        });
        const { status, errorCode  } = response.data;

        if (status === 200) {
          await dispatch('fetchDepartmentsFromCatalog');
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyDepartmentInCatalog({dispatch}, { id, name, parentId, level }) {
      try {
        const response = await api.put(`/system/department/${id}`, {
          name,
          parentId,
          level
        });
        const { status, errorCode  } = response.data;

        if (status === 200) {
          await dispatch('fetchDepartmentsFromCatalog');
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },


    async fetchAccreditationsDocumentsFromCatalog({commit}) {
      try {
        const response = await api.get('/system/documents/Accreditation');
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setAccreditationDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async deleteAccreditationDocumentFromCatalog({commit}, { id }) {
      try {
        const response = await api.delete(`/system/document/${id}/Accreditation`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setAccreditationDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addAccreditationDocumentToCatalog({commit}, { name, organization, ip, person, passport, num }) {
      try {
        const response = await api.put(`/system/document/0/Accreditation`, {
          name,
          organization,
          ip,
          person,
          passport,
          num
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setAccreditationDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyAccreditationsDocumentInCatalog({commit}, { id, name, organization, ip, person, passport, num }) {
      try {
        const response = await api.put(`/system/document/${id}/Accreditation`, {
          name,
          organization,
          ip,
          person,
          passport,
          num
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setAccreditationDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },



    async fetchTenderDocumentsFromCatalog({commit}) {
      try {
        const response = await api.get('/system/documents/Tender');
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setTenderDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async deleteTenderDocumentFromCatalog({commit}, { id }) {
      try {
        const response = await api.delete(`/system/document/${id}/Tender`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setTenderDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addTenderDocumentToCatalog({commit}, { name, passport, num }) {
      try {
        const response = await api.put(`/system/document/0/Tender`, {
          name,
          organization: false,
          ip: false,
          person: false,
          passport,
          num
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setTenderDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyTenderDocumentInCatalog({commit}, { id, name, passport, num }) {
      try {
        const response = await api.put(`/system/document/${id}/Tender`, {
          name,
          organization: false,
          ip: false,
          person: false,
          passport,
          num
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setTenderDocuments', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },





    async fetchLotNumbersFromCatalog({commit}, {taskNumber, availability, owner, page, pageSize}) {
      try {
        page = page || 1;
        pageSize = pageSize || 10;

        const response = await api.get('/system/lotNums', {
          params: {
            num: taskNumber,
            organizer: owner,
            used: availability,
            page: page,
            pageSize: pageSize,
          }
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setLotNumbers', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async deleteLotNumberFromCatalog({commit}, { id }) {
      try {
        const response = await api.delete(`/system/lotNum/${id}`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setLotNumbers', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addLotNumberToCatalog({commit}, { num, numTask, time }) {
      try {
        const response = await api.put(`/system/lotNum/0`, {
          num,
          numTask: numTask || undefined,
          time: +time || undefined
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setLotNumbers', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyLotNumberInCatalog({commit}, { id, num, numTask, time, taskNumber, availability, owner, page, pageSize }) {
      try {
        const response = await api.put(`/system/lotNum/${id}`, {
          num,
          numTask: numTask || undefined,
          time: +time || undefined
        }, {
          params: {
            num: taskNumber,
            organizer: owner,
            used: availability,
            page: page,
            pageSize: pageSize,
          }
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setLotNumbers', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },






    async fetchExpertCriteriaFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/criterions/Expert');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setExpertCriteria', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async fetchPriceCriteriaFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/criterions/Price');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setPriceCriteria', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async fetchWeightedCriteriaFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/criterions/Reduction');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setWeightedCriteria', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async deleteCriteriaFromCatalog({commit}, { id, type }) {
      try {
        const response = await api.delete(`/system/criterion/${id}`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          if (type === 'expert') {
            commit('setExpertCriteria', result);
          }
          else if (type === 'reduction') {
            commit('setWeightedCriteria', result);
          }
          else {
            commit('setPriceCriteria', result);
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addCriteriaToCatalog({commit}, { name, isText, isFile, type }) {
      let error;

      try {
        let valueType;

        if (isText && !isFile) {
          valueType = 'Text'
        }
        else if (isFile && !isText) {
          valueType = 'File'
        }
        else if (isFile && isText) {
          valueType = 'All'
        }

        if (type === 'expert') {
          type = 'Expert'
        }
        else if (type === 'reduction') {
          type = 'Reduction'
        }
        else if (type === 'price') {
          type = 'Price'
        }

        const response = await api.put(`/system/criterion/0`, {
          name,
          type,
          valueType
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          if (type === 'Expert') {
            commit('setExpertCriteria', result);
          }
          else if (type === 'Reduction') {
            commit('setWeightedCriteria', result);
          }
          else if (type === 'Price') {
            commit('setPriceCriteria', result);
          }
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error
      }
    },

    async modifyCriteriaInCatalog({commit}, { id, name, isText, isFile, type }) {
      let error;

      try {
        let valueType;

        if (isText && !isFile) {
          valueType = 'Text'
        }
        else if (isFile && !isText) {
          valueType = 'File'
        }
        else if (isFile && isText) {
          valueType = 'All'
        }

        if (type === 'expert') {
          type = 'Expert'
        }
        else if (type === 'reduction') {
          type = 'Reduction'
        }
        else if (type === 'price') {
          type = 'Price'
        }

        const response = await api.put(`/system/criterion/${id}`, {
          name,
          type,
          valueType
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          if (type === 'Expert') {
            commit('setExpertCriteria', result);
          }
          if (type === 'Reduction') {
            commit('setWeightedCriteria', result);
          }
          else if (type === 'ReducedPrice') {
            commit('setPriceCriteria', result);
          }
          else {
            commit('setPriceCriteria', result);
          }
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },





    async fetchFileExtensionsFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/fileExtensions/admin');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setFileExtensions', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async deleteFileExtensionFromCatalog({commit}, { id }) {
      try {
        const response = await api.delete(`/system/fileExtension/${id}`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setFileExtensions', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addFileExtensionToCatalog({commit}, { extension, maxSize }) {
      try {
        const response = await api.put(`/system/fileExtension/0`, {
          extension,
          maxSize
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setFileExtensions', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyFileExtensionInCatalog({commit}, { id, extension, maxSize }) {
      try {
        const response = await api.put(`/system/fileExtension/${id}`, {
          extension,
          maxSize
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setFileExtensions', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },




    async fetchAdditionalExpensesFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/additionalNomens');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setAdditionalExpenses', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async handleChangeInAdditionalExpenses({commit, dispatch}, data) {
      commit('setAdditionalExpenses', data);

      await dispatch('fetchAdditionalBidRulesFromCatalog');
    },

    async deleteAdditionalExpensesFromCatalog({dispatch}, { id }) {
      try {
        const response = await api.delete(`/system/additionalNomen/${id}`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          await dispatch('handleChangeInAdditionalExpenses', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addAdditionalExpensesToCatalog({dispatch}, { code, name, formula }) {
      try {
        const response = await api.put(`/system/additionalNomen/0`, {
          code,
          name,
          percent: formula.valueOf() === api.BidRuleFormula.PERCENT
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          await dispatch('handleChangeInAdditionalExpenses', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyAdditionalExpensesInCatalog({dispatch}, { id, code, name, formula }) {
      try {
        const response = await api.put(`/system/additionalNomen/${id}`, {
          code,
          name,
          percent: formula.valueOf() === api.BidRuleFormula.PERCENT
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          await dispatch('handleChangeInAdditionalExpenses', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },


    async fetchBidRulesFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/bidRules');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setBidRules', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async deleteBidRuleFromCatalog({commit}, { id }) {
      try {
        const response = await api.delete(`/system/bidRule/${id}`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setBidRules', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addBidRuleToCatalog({commit}, data) {
      try {
        const response = await api.put(`/system/bidRule/0`, {
          bidByn: data.bidCurrency.valueOf(),
          bidRuleComment: data.bidRuleComment,
          bidRuleUserComment: data.bidRuleUserComment,
          currencyClause: data.currencyClause.valueOf(),
          fixBynComment: data.fixBynComment,
          fixDate: data.fixDate.valueOf(),
          payByn: data.contractCurrency.valueOf(),
          payBynComment: data.paymentCurrency,
          payDate: data.paymentDate.valueOf(),
          resident: data.status.valueOf(),
          type: data.type.valueOf(),
          allowAdvance: !!data.allowAdvance
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setBidRules', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyBidRuleInCatalog({commit}, data) {
      try {
        const response = await api.put(`/system/bidRule/${data.id}`, {
          bidByn: data.bidCurrency.valueOf(),
          bidRuleComment: data.bidRuleComment,
          bidRuleUserComment: data.bidRuleUserComment,
          currencyClause: data.currencyClause.valueOf(),
          fixBynComment: data.fixBynComment,
          fixDate: data.fixDate.valueOf(),
          payByn: data.contractCurrency.valueOf(),
          payBynComment: data.paymentCurrency,
          payDate: data.paymentDate.valueOf(),
          resident: data.status.valueOf(),
          type: data.type.valueOf(),
          allowAdvance: !!data.allowAdvance
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setBidRules', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },




    async fetchAdditionalBidRulesFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/additionalBidRules');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setAdditionalBidRules', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async addAdditionalBidRuleToCatalog({dispatch}, { additionalExpense, data }) {
      await dispatch('_addAdditionalBidRuleToCatalog', { additionalExpense, data });
    },

    async modifyAdditionalBidRuleToCatalog({dispatch}, { additionalExpense, data }) {
      await dispatch('_addAdditionalBidRuleToCatalog', { additionalExpense, data });
    },

    async _addAdditionalBidRuleToCatalog({commit}, { additionalExpense, data }) {
      let error;

      try {
        const id = data.id || 0;
        const response = await api.put(`/system/additionalBidRule/${id}`, {
          nomen: additionalExpense,
          bidByn: data.bidCurrency.valueOf(),
          bidRuleComment: data.bidRuleComment,
          bidRuleUserComment: data.bidRuleUserComment,
          currencyClause: data.currencyClause.valueOf(),
          fixBynComment: data.fixBynComment,
          fixDate: data.fixDate.valueOf(),
          payByn: data.contractCurrency.valueOf(),
          payBynComment: data.paymentCurrency,
          payDate: data.paymentDate.valueOf(),
          resident: data.status.valueOf(),
          type: data.type.valueOf(),
          allowAdvance: !!data.allowAdvance,
          rule: data.rule
        });
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setAdditionalBidRules', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async deleteAdditionalBidRuleToCatalog({commit}, {id}) {
      let error;

      try {
        const response = await api.delete(`/system/additionalBidRule/${id}`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setAdditionalBidRules', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },




    async fetchContractorsFromCatalog({commit}, options) {
      let error;

      try {
        const page = _.get(options, 'page', 1);
        const pageSize =  _.get(options, 'pageSize', 10);
        const text = _.get(options, 'text');

        const response = await api.get('/system/customers', {
          params: {
            page: page,
            pageSize: pageSize,
            query: LayoutConverter.en(text),
            query2: LayoutConverter.ru(text)
          }
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setContractors', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async syncContractorsInCatalog() {
      let error;

      try {
        const response = await api.get('/system/sync/CUSTOMER');

        const { status, errorCode  } = response.data;

        if (status !== 200) {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },



    async syncCategoriesInCatalog() {
      let error;

      try {
        const response = await api.get('/system/sync/CATEGORY');

        const { status, errorCode  } = response.data;

        if (status !== 200) {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },


    async fetchTemplates({commit}) {
      let error;

      try {
        const response = await api.get(`/reports/reportTemplates`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setTemplates', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadAttachedTemplate({commit}, { type, extension }) {
      let error;

      try {
        await api.download(`/reports/${type}`);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async updateTemplate({dispatch}, { type, description, file }) {
      let error;

      try {
        const data = new FormData();
        data.append('description', description);

        if (file) {
          data.append('file', file);
        }

        const response = await api.put(`/reports/${type}`, data);
        const { status, errorCode } = response.data;

        if (status === 200) {
          dispatch('fetchTemplates');
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },


    async fetchNomenclatureCatalog({commit, dispatch}, parent) {
      let error;

      try {
        const data = await dispatch('fetchCategories', { parent });

        commit('setNomenclature', {
          parent,
          items: data
        });
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async toggleNomenclatureItem({commit, dispatch}, item) {
      let error;

      try {
        if (!item.fetchedChildren) {
          await dispatch('fetchNomenclatureCatalog', item.id);
        }
        commit('toggleNomenclature', item);
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async syncNomenclatureWith1C({commit, dispatch}) {
      let error;

      try {
        const response = await api.get(`/system/sync/CATEGORY`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('clearNomenclatureCatalog');
          await dispatch('fetchNomenclatureCatalog');
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },






    async fetchRegulationsParagraphsFromCatalog({commit}) {
      let error;

      try {
        const response = await api.get('/system/reglamentReasons');
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          commit('setRegulationsParagraphs', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async deleteRegulationsParagraphFromCatalog({commit}, { id }) {
      try {
        const response = await api.delete(`/system/reglamentReason/${id}`);
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setRegulationsParagraphs', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async addRegulationsParagraphToCatalog({commit}, { code, description }) {
      try {
        const response = await api.put(`/system/reglamentReason/0`, {
          code,
          description
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setRegulationsParagraphs', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async modifyRegulationsParagraphInCatalog({commit}, { id, code, description }) {
      try {
        const response = await api.put(`/system/reglamentReason/${id}`, {
          code,
          description
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setRegulationsParagraphs', result);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },
   async fetchBlackBox({commit}, options) {
      let error;

      try {
        const page = _.get(options, 'page', 1);
        const pageSize =  _.get(options, 'pageSize', 10);

        const response = await api.get('/system/blackBox', {
          params: {
            page: page,
            pageSize: pageSize,
          }
        });
        const { status, result, errorCode  } = response.data;

        if (status === 200) {
          commit('setBlackBox', result);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },


  }
}
